import React from 'react';
import { FaEnvelope } from 'react-icons/fa';
import { Field, Document } from '@api';

// Determine default locale: prefer runtime override set in window (main.tsx), then Vite env, then navigator, then fallback to 'nl-NL'
export const DEFAULT_LOCALE: string = (typeof window !== 'undefined' && (window as any).__DEFAULT_LOCALE__) || (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_DEFAULT_LOCALE) || (typeof navigator !== 'undefined' && navigator.language) || 'nl-NL';

export const OPERATORS = [
  { value: 'eq', label: '=' },
  { value: 'ne', label: '\u2260' }, // not equal
  { value: 'contains', label: 'contains' },
  { value: 'startswith', label: 'starts with' },
  { value: 'endswith', label: 'ends with' },
  { value: 'between', label: 'between' },
  { value: 'gt', label: '>' },
  { value: 'ge', label: '\u2265' },
  { value: 'lt', label: '<' },
  { value: 'le', label: '\u2264' },
  { value: 'empty', label: 'empty' },
  { value: 'notempty', label: 'not empty' }
];

const OPERATORS_BY_VALUE = new Map(OPERATORS.map(o => [o.value, o]));

export function getOperatorsForType(type: string) {
  const t = (type || '').toLowerCase();
  // Text fields: =, <>, startswith, endswith, contains, empty, notempty
  if (t === 'text' || t === 'string') {
    return ['eq','ne','startswith','endswith','contains','empty','notempty'].map(v => OPERATORS_BY_VALUE.get(v)!).filter(Boolean);
  }
  // Numeric fields: =, <>, between, >, <, <=, >=, empty, notempty
  if (['number','int','integer','float','decimal','currency'].includes(t)) {
    return ['eq','ne','between','gt','lt','le','ge','empty','notempty'].map(v => OPERATORS_BY_VALUE.get(v)!).filter(Boolean);
  }
  // Date fields: same as numeric
  if (t === 'date' || t === 'datetime') {
    return ['eq','ne','between','gt','lt','le','ge','empty','notempty'].map(v => OPERATORS_BY_VALUE.get(v)!).filter(Boolean);
  }
  // Default to text-like
  return ['eq','ne','startswith','endswith','contains','empty','notempty'].map(v => OPERATORS_BY_VALUE.get(v)!).filter(Boolean);
}

export function getOperatorsForField(field?: Field) {
  if (!field) return getOperatorsForType('text');
  const tp = field.datatype?.toLowerCase();
  if (['int','integer','float','double','decimal','currency','number'].includes(tp || '')) return getOperatorsForType('number');
  if (tp === 'date' || tp === 'datetime') return getOperatorsForType('date');
  // boolean/checkbox only allow eq/ne/empty/notempty
  if (tp === 'bool' || tp === 'boolean') return ['eq','ne','empty','notempty'].map(v => OPERATORS_BY_VALUE.get(v)!).filter(Boolean);
  return getOperatorsForType('text');
}

export function getRowValues(doc: Document, keys: string[]) {
  function readValue(obj: any, key: string) {
    if (!obj || key == null) return undefined;
    if (Object.prototype.hasOwnProperty.call(obj, key)) return obj[key];
    const target = String(key).toLowerCase();
    const actual = Object.keys(obj).find(k => k.toLowerCase() === target);
    return actual ? obj[actual] : undefined;
  }

  const anyDoc = doc as any;
  const dv = anyDoc?.documentValues ?? anyDoc?.DocumentValues;
  const topFolder = anyDoc?.topFolder ?? anyDoc?.TopFolder;
  const tfv = topFolder?.folderValues ?? topFolder?.FolderValues;
  const folder = anyDoc?.folder ?? anyDoc?.Folder;
  const fv = folder?.folderValues ?? folder?.FolderValues;

  return keys.map(key => {
    const k = (key || '').toLowerCase();
    const fromDocValues = readValue(dv, key);
    if (fromDocValues !== undefined) return fromDocValues;

    // Folder values can come back either on topFolder (search results) or folder
    const fromTopFolderValues = readValue(tfv, key);
    if (fromTopFolderValues !== undefined) return fromTopFolderValues;

    const fromFolderValues = readValue(fv, key);
    if (fromFolderValues !== undefined) return fromFolderValues;

    // Built-in meta fields
    if (k === 'description' || k === 'document_description' || k === 'documentdescription') return (doc as any).documentDescription ?? (doc as any).DocumentDescription ?? '';
    if (k === 'createdate' || k === 'documentdatecreated') return (doc as any).documentDateCreated ?? (doc as any).DocumentDateCreated ?? '';
    if (k === 'documentid' || k === 'id') return (doc as any).id ?? '';
    if (k === 'subfolder') return (doc as any).folder?.folderDescription ?? (doc as any).Folder?.FolderDescription ?? '';

    return '';
  });
}

// Inline simple badge icon
function Badge({ text, color }: { text: string; color: string }) {
  return (
    <span style={{ display:'inline-flex', alignItems:'center', justifyContent:'center', width:24, height:16, borderRadius:4, border:`2px solid ${color}`, fontSize:9, fontWeight:600, color, fontFamily:'Inter,Arial,sans-serif' }}>{text}</span>
  );
}
export function getDocumentIcon(extension: string) {
    const ext = (extension || '').toLowerCase();
    if (ext === 'pdf') return <Badge text="PDF" color="#E53935" />;
    if (ext === 'doc' || ext === 'docx') return <Badge text="DOC" color="#1976D2" />;
    if (ext === 'xls' || ext === 'xlsx') return <Badge text="XLS" color="#388E3C" />;
    if (ext === 'eml' || ext === 'msg') return <FaEnvelope size={20} color="#6D4C41" title="Email" />;

    // Show the extension in the badge (uppercase, max 4 chars). Fallback to 'FILE' when empty.
    const text = ext ? ext.substring(0, 4).toUpperCase() : 'FILE';
    return <Badge text={text} color="#757575"/>;
}

function getMRUKey(drawerId: number | null, fieldName: string) { return `searchMRU_${drawerId ?? 'none'}_${fieldName}`; }
export function getMRU(drawerId: number | null, fieldName: string): string[] { try { const raw = localStorage.getItem(getMRUKey(drawerId, fieldName)); return raw ? (JSON.parse(raw) as string[] ?? []) : []; } catch { return []; } }
export function addMRU(drawerId: number | null, fieldName: string, value: string) { if (!value) return; const key = getMRUKey(drawerId, fieldName); const cur = getMRU(drawerId, fieldName).filter(v=>v!==value); cur.unshift(value); localStorage.setItem(key, JSON.stringify(cur.slice(0,10))); }

export function getInputType(field: Field): string { const type = field.datatype?.toLowerCase(); if (["number","int","float","decimal"].includes(type||'')) return 'number'; if (type === 'date' || type === 'datetime') return 'date'; if (type === 'bool' || type === 'boolean') return 'checkbox'; return 'text'; }
// Treat currency as amount (format with decimals). Decimal should be treated as numeric without forcing decimals.
export function isAmountField(field: Field | undefined) { if (!field) return false; const name = field.name.toLowerCase(); const dt = field.datatype?.toLowerCase(); // only currency or heuristics are amounts
  const isCurrency = dt === 'currency';
  if (isCurrency) return true;
  return ['amount','prijs','price','total','cost','totaal','bedrag'].some(w=> name.includes(w));
}
export function isNumericField(field: Field | undefined) { if (!field) return false; return ['int','integer','float','double','decimal','currency'].includes(field.datatype?.toLowerCase()||''); }
export function isDateField(field: Field | undefined) { if (!field) return false; const t = field.datatype?.toLowerCase(); return t==='date'||t==='datetime'; }

function parseCompactDate(s: string): Date | null {
  // Accept formats like YYYYMMDD, YYYYMMDDHHMM, YYYYMMDDHHMMSS
  if (!/^[0-9]{8,14}$/.test(s)) return null;
  const year = Number(s.substr(0,4));
  const month = Number(s.substr(4,2));
  const day = Number(s.substr(6,2));
  let hour = 0, minute = 0, second = 0;
  if (s.length >= 12) {
    hour = Number(s.substr(8,2));
    minute = Number(s.substr(10,2));
  }
  if (s.length === 14) {
    second = Number(s.substr(12,2));
  }
  // basic validation
  if (month < 1 || month > 12) return null;
  if (day < 1 || day > 31) return null;
  const d = new Date(year, month - 1, day, hour, minute, second);
  if (isNaN(d.getTime())) return null;
  return d;
}

export function formatCell(field: Field | undefined, val: any) { 
  // Handle compact date strings like 20190130120000
    if (isDateField(field) && typeof val === 'string' && /^\d{14}/.test(val)) {
    const compact = parseCompactDate(val.trim());
    if (compact) {
      // If time components are zero, show date only, else show date+time
      if (compact.getHours() === 12 && compact.getMinutes() === 0 && compact.getSeconds() === 0) {
        return compact.toLocaleDateString(DEFAULT_LOCALE, { day: '2-digit', month: '2-digit', year: 'numeric' });
      }
      return compact.toLocaleString(DEFAULT_LOCALE, { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });
    }
  }

    if (isDateField(field) && typeof val === 'string' && /^\d{4}\-\d{2}\-\d{2}(T00:00:00)?/.test(val)) {
    const part = val.split('T')[0]; if(part){ const d = new Date(part); if(!isNaN(d.getTime())) return d.toLocaleDateString(DEFAULT_LOCALE,{day:'2-digit',month:'2-digit',year:'numeric'}); }
  }
  if (val == null || val === '') return '';
  // Currency: always show two decimals
  if (isAmountField(field)) {
    const num = typeof val === 'number' ? val : Number(val);
    if (!isNaN(num)) return num.toFixed(2);
    return val;
  }
  // Other numeric types (including 'decimal'): don't force two decimals; show raw numeric value
  if (isNumericField(field)) {
    const num = typeof val === 'number' ? val : Number(val);
    if (!isNaN(num)) {
      // Integer -> no decimals, otherwise preserve original precision
      if (Number.isInteger(num)) return String(num);
      return String(num);
    }
  }
  return val;
}
